import type { ComponentProps, CSSProperties, ReactNode } from 'react';

import { Button } from '@actual-app/components/button';
import { css } from '@emotion/css';

import { zonaTactil } from './tactil';
import { color, deshabilitado, movimiento, radio } from './tokens';

export type VarianteBoton =
  | 'primario'
  | 'tonal'
  | 'fantasma'
  | 'aviso'
  | 'peligro';

const VARIANTES: Record<VarianteBoton, { fondo: string; texto: string }> = {
  primario: { fondo: color.accent, texto: color.accentInk },
  tonal: { fondo: color.badSoft, texto: color.bad },
  aviso: { fondo: color.warnSoft, texto: color.warn },
  fantasma: { fondo: color.surface2, texto: color.fg },
  /** Rojo sólido: la acción borra, cierra o desconecta (B6 · H73). */
  peligro: { fondo: color.bad, texto: '#ffffff' },
};

type BotonProps = {
  variante?: VarianteBoton;
  /** Ancho completo, 54 px de alto y radio 18 (Guardar, Asignar…). */
  bloque?: boolean;
  /**
   * Zona táctil ampliada (px CSS) sin cambiar el aspecto (B2 · H04): para
   * los botones de uso diario en filas con hueco (p. ej. 52 en «Cubrir»).
   */
  zona?: number;
  children: ReactNode;
  style?: CSSProperties;
} & Omit<ComponentProps<typeof Button>, 'variant' | 'style' | 'children'>;

/** Botón del sistema A: 44 px mínimo, radio 14, peso 800. */
export function Boton({
  variante = 'primario',
  bloque = false,
  zona,
  children,
  style,
  ...props
}: BotonProps) {
  const { fondo, texto } = VARIANTES[variante];
  return (
    <Button
      variant="bare"
      {...props}
      {...(zona
        ? { className: css(zonaTactil(zona)), 'data-zona-tactil': zona }
        : null)}
      style={({ isPressed, isHovered, isDisabled }) => ({
        minHeight: bloque ? 54 : 44,
        width: bloque ? '100%' : undefined,
        borderRadius: bloque ? 18 : radio.boton,
        padding: '0 16px',
        fontWeight: 800,
        fontSize: bloque ? 16 : 14,
        gap: 8,
        flexShrink: 0,
        // H05/H42: estilo común de deshabilitado (tokens.deshabilitado).
        backgroundColor: isDisabled ? deshabilitado.fondo : fondo,
        color: isDisabled ? deshabilitado.texto : texto,
        cursor: isDisabled ? 'default' : undefined,
        border: 0,
        filter: isHovered ? 'brightness(1.06)' : undefined,
        transform: isPressed ? 'scale(0.97)' : undefined,
        transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
        // El botón base recorta (overflow hidden) y ocultaría la zona.
        ...(zona ? { overflow: 'visible' } : null),
        ...style,
      })}
    >
      {children}
    </Button>
  );
}
